import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface PermissionSet {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  scope?: string;
}

interface Permissions {
  [resource: string]: PermissionSet;
}

interface PermissionsCacheState {
  userKey: string | null;
  permissions: Permissions | null;
  fetchedAt: number;
  inflight: Promise<Permissions> | null;
}

const PERMISSIONS_CACHE_TTL_MS = 5 * 60 * 1000;

const permissionsCache: PermissionsCacheState = {
  userKey: null,
  permissions: null,
  fetchedAt: 0,
  inflight: null
};

const getUserCacheKey = (user: any): string => {
  return user?.id || user?.email || 'anonymous';
};

interface UseDynamicPermissionsReturn {
  permissions: Permissions;
  loading: boolean;
  error: Error | null;
  can: (resource: string, action: 'create' | 'read' | 'update' | 'delete') => boolean;
  hasPermission: (resource: string, action: string) => boolean;
  hasAnyPermission: (permissions: Array<{ resource: string; action: string }>) => boolean;
  hasAllPermissions: (permissions: Array<{ resource: string; action: string }>) => boolean;
  getScope: (resource: string) => string | undefined;
  canAccessResource: (resource: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

export function useDynamicPermissions(): UseDynamicPermissionsReturn {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Permissions>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      permissionsCache.userKey = null;
      permissionsCache.permissions = null;
      permissionsCache.fetchedAt = 0;
      permissionsCache.inflight = null;
      setPermissions({});
      setLoading(false);
      return;
    }

    const userKey = getUserCacheKey(user);

    if (permissionsCache.userKey !== userKey) {
      permissionsCache.userKey = userKey;
      permissionsCache.permissions = null;
      permissionsCache.fetchedAt = 0;
      permissionsCache.inflight = null;
    }

    const cacheAge = Date.now() - permissionsCache.fetchedAt;
    const hasFreshCache =
      permissionsCache.permissions !== null && cacheAge < PERMISSIONS_CACHE_TTL_MS;

    if (hasFreshCache) {
      setPermissions(permissionsCache.permissions as Permissions);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!permissionsCache.inflight) {
        permissionsCache.inflight = api
          .get('/permissions/my')
          .then((response) => {
            const fetchedPermissions = response.data.permissions || {};
            permissionsCache.permissions = fetchedPermissions;
            permissionsCache.fetchedAt = Date.now();
            return fetchedPermissions;
          })
          .finally(() => {
            permissionsCache.inflight = null;
          });
      }

      const fetchedPermissions = await permissionsCache.inflight;
      setPermissions(fetchedPermissions);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch permissions:', err);

      // Set default permissions on error
      permissionsCache.permissions = null;
      permissionsCache.fetchedAt = 0;
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const can = useCallback((resource: string, action: 'create' | 'read' | 'update' | 'delete'): boolean => {
    // GOD role (super_admin) can do everything
    if ((user as any)?.roleType === 'GOD') {
      return true;
    }

    // Admin, Manager, User, Contact use database permissions
    const resourcePermissions = permissions[resource];
    if (!resourcePermissions) {
      return false;
    }

    return resourcePermissions[action] || false;
  }, [permissions, user]);

  const hasPermission = useCallback((resource: string, action: string): boolean => {
    if (action === 'create' || action === 'read' || action === 'update' || action === 'delete') {
      return can(resource, action);
    }
    return false;
  }, [can]);

  const hasAnyPermission = useCallback((permissionChecks: Array<{ resource: string; action: string }>): boolean => {
    return permissionChecks.some(check => hasPermission(check.resource, check.action));
  }, [hasPermission]);

  const hasAllPermissions = useCallback((permissionChecks: Array<{ resource: string; action: string }>): boolean => {
    return permissionChecks.every(check => hasPermission(check.resource, check.action));
  }, [hasPermission]);

  const getScope = useCallback((resource: string): string | undefined => {
    return permissions[resource]?.scope;
  }, [permissions]);

  const canAccessResource = useCallback((resource: string): boolean => {
    // If user can read the resource, they can access it
    return can(resource, 'read');
  }, [can]);

  const refreshPermissions = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  return {
    permissions,
    loading,
    error,
    can,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getScope,
    canAccessResource,
    refreshPermissions
  };
}

// Helper HOC for protecting components with permissions
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  resource: string,
  action: 'create' | 'read' | 'update' | 'delete',
  FallbackComponent?: React.ComponentType
) {
  return function PermissionProtectedComponent(props: P) {
    const { can, loading } = useDynamicPermissions();

    if (loading) {
      return (
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      );
    }

    if (!can(resource, action)) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      return (
        <div className="text-center p-4">
          <p className="text-gray-500">You don't have permission to {action} {resource}.</p>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

// Component for conditional rendering based on permissions
interface PermissionGateProps {
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete';
  children: React.ReactNode;
  fallback?: React.ReactNode;
  hideWhenUnauthorized?: boolean;
}

export function PermissionGate({ 
  resource, 
  action, 
  children, 
  fallback = null,
  hideWhenUnauthorized = true 
}: PermissionGateProps) {
  const { can, loading } = useDynamicPermissions();

  if (loading) {
    return null;
  }

  if (!can(resource, action)) {
    if (hideWhenUnauthorized) {
      return null;
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
}